import React, { useEffect, useRef, useState } from "react";

/**
 * A piece of text to copy, and the button that copies it, on one row. When
 * the browser refuses the clipboard (an insecure page, a denied permission)
 * the text is selected instead, so Ctrl/Cmd+C finishes the job; nothing throws.
 *
 * Two shapes: a read-only field for the link, and one truncated line of dark
 * code for the embed snippet — the whole snippet is what is copied either way.
 */
export const CopyBox: React.FC<{
  text: string;
  buttonLabel: string;
  /** The dark one-line code block, rather than the read-only field. */
  code?: boolean;
  ariaLabel: string;
  /** For a visible <label htmlFor> on the field. */
  id?: string;
}> = ({ text, buttonLabel, code, ariaLabel, id }) => {
  const field = useRef<HTMLInputElement | null>(null);
  const block = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<"idle" | "copied" | "select">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const settle = (next: "copied" | "select") => {
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), next === "copied" ? 2000 : 4000);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      settle("copied");
    } catch {
      if (field.current) {
        field.current.focus();
        field.current.select();
      } else if (block.current) {
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          range.selectNodeContents(block.current);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      settle("select");
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-stretch gap-1.5">
        {code ? (
          <code
            ref={block}
            id={id}
            aria-label={ariaLabel}
            title={text}
            className="min-w-0 flex-1 select-all truncate rounded-[10px] bg-ink-100 px-3 py-[11px] font-mono text-[12px] text-bay-800"
          >
            {text}
          </code>
        ) : (
          <input
            ref={field}
            id={id}
            readOnly
            value={text}
            aria-label={id ? undefined : ariaLabel}
            onFocus={(event) => event.currentTarget.select()}
            className="min-w-0 flex-1 rounded-[10px] border border-[var(--edge-strong)] bg-bay-900 px-3 py-[10px] font-mono text-[13px] text-ink-100 focus:border-[var(--edge-focus)] focus:outline-none"
          />
        )}
        <button
          type="button"
          className={`btn shrink-0 !h-auto !rounded-[10px] px-4 ${code ? "btn-secondary" : "bg-ink-100 text-white hover:bg-ink-200"}`}
          onClick={() => void copy()}
        >
          {state === "copied" ? "Copied" : buttonLabel}
        </button>
      </div>
      {state === "select" ? (
        <span role="status" className="text-[11.5px] text-ink-300">
          Couldn't copy automatically. It's selected; press Ctrl/Cmd+C.
        </span>
      ) : null}
    </div>
  );
};
